import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  MilestoneIssueDetails,
  ProcessContainer,
  ProcessData,
  ProcessInfo,
  ProcessItem as StyledProcessItem,
  StepStatus,
  StepsContainer,
} from 'domain/shared/timeline';
import moment from 'moment';
import { DATE_FORMAT, useDateTimeService } from 'framework/utils';
import { LEAVE_TYPES, LEGACY_LEAVE_TYPE_LABELS } from 'domain/shared/leave';
import MilestoneItem from './MilestoneItem';

interface ProcessItemProps {
  process: ProcessData;
  showTitle?: boolean;
}

const LEAVE_PROCESS_PREFIX = 'HR3_Request_Leave';
const LEAVE_DATE_REGEX = /from\s+([A-Za-z]{3}\s+\d{1,2},\s+\d{4})/i;

const PROCESS_LEAVE_TYPE_LABELS = {
  ...LEAVE_TYPES,
  ...LEGACY_LEAVE_TYPE_LABELS,
} as const;

const getLeaveTypeFromBusinessKey = (businessKey: string) =>
  Object.keys(PROCESS_LEAVE_TYPE_LABELS).find(type => new RegExp(`\\b${type}\\b`).test(businessKey));

const getRequestDate = (process: ProcessData) => {
  const leaveDateMatch = process.processBusinessKey.match(LEAVE_DATE_REGEX)?.[1];
  if (leaveDateMatch) {
    const parsedLeaveDate = moment(leaveDateMatch, DATE_FORMAT.NATURAL_SHORT, true);
    if (parsedLeaveDate.isValid()) {
      return parsedLeaveDate.format(DATE_FORMAT.NATURAL_SHORT);
    }
  }

  return moment(process.startedAt).format(DATE_FORMAT.NATURAL_SHORT);
};

const formatProcessTitle = (process: ProcessData) => {
  if (!process.processDefinition.startsWith(LEAVE_PROCESS_PREFIX)) {
    return process.processBusinessKey;
  }

  const mappedLeaveType = process.additionalInfo?.type
    ? PROCESS_LEAVE_TYPE_LABELS[process.additionalInfo.type as keyof typeof PROCESS_LEAVE_TYPE_LABELS]
    : undefined;
  const leaveTypeFromBusinessKey = getLeaveTypeFromBusinessKey(process.processBusinessKey);
  const leaveTypeLabel =
    mappedLeaveType ||
    (leaveTypeFromBusinessKey
      ? PROCESS_LEAVE_TYPE_LABELS[leaveTypeFromBusinessKey as keyof typeof PROCESS_LEAVE_TYPE_LABELS]
      : null);

  return `${leaveTypeLabel || 'Leave'} starting on ${getRequestDate(process)}`;
};

const getProcessingText = (process: ProcessData) => {
  const activeMilestone = process.milestonesDetails.find(milestone => milestone.status === StepStatus.ACTIVE);
  if (!activeMilestone) {
    return null;
  }

  const detailsList = Array.isArray(activeMilestone.detailsList)
    ? activeMilestone.detailsList
    : activeMilestone.details
      ? [activeMilestone.details]
      : [];

  if (detailsList.length === 0) {
    return null;
  }

  const assignees = detailsList
    .map((detail: MilestoneIssueDetails) => detail.assignee)
    .filter((name: string | null): name is string => Boolean(name && name.trim()));
  const assigneesText = assignees.length > 0 ? assignees.join(', ') : null;

  const overdue = Number(detailsList[0].overdue);
  const hasOverdue = Number.isFinite(overdue) && overdue > 0;
  const overdueSuffix = hasOverdue ? ` (${overdue} business ${overdue === 1 ? 'day' : 'days'} late)` : '';

  return assigneesText
    ? `Request is now processed by ${assigneesText}${overdueSuffix}`
    : `Request is now being processed${overdueSuffix}`;
};

const ProcessItem: React.FC<ProcessItemProps> = ({ process, showTitle = true }) => {
  const { fromNow } = useDateTimeService();
  const processingText = getProcessingText(process);
  const fromNowWithoutLeadingSpace = (dateUTC: string) => fromNow(dateUTC).trimStart();

  return (
    <ProcessContainer key={process.processDefinition}>
      {showTitle && (
        <Typography variant="subtitle1" mb={1}>
          {formatProcessTitle(process)}
        </Typography>
      )}
      <StyledProcessItem>
        <ProcessInfo>
          <Box>
            <Typography variant="subtitle2">
              Started on {moment(process.startedAt).format(DATE_FORMAT.SIMPLE)} (
              {fromNowWithoutLeadingSpace(process.startedAt)})
            </Typography>
            {processingText && (
              <Typography variant="caption" color="textSecondary" display="block">
                {processingText}
              </Typography>
            )}
          </Box>
        </ProcessInfo>

        <StepsContainer>
          {process.milestonesDetails.map(milestone => (
            <MilestoneItem key={milestone.milestoneName} milestone={milestone} />
          ))}
        </StepsContainer>
      </StyledProcessItem>
    </ProcessContainer>
  );
};

export default ProcessItem;
